import { isNativePlatform } from '@/lib/platform';

// Stable per-device id. Priority order:
//   1. Native (Capacitor Device.getId) — on Android returns Settings.Secure.ANDROID_ID,
//      which survives APK reinstalls (only changes on factory reset). On iOS returns
//      identifierForVendor, which persists across reinstalls as long as another app
//      from the same vendor is present, otherwise per-install.
//   2. localStorage (web / native fallback).
//   3. Freshly generated UUID.
// The resolved id is mirrored to both localStorage AND @capacitor/preferences so the
// sync getter (used by any legacy caller) still returns the same value on next boot.
export const DEVICE_ID_KEY = 'lr_device_id_v1';

const generateId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const writeLocal = (value: string): void => {
  try { localStorage.setItem(DEVICE_ID_KEY, value); } catch { /* ignore */ }
};

const mirrorToNativePrefs = async (value: string): Promise<void> => {
  if (!isNativePlatform()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: DEVICE_ID_KEY, value });
  } catch (e) {
    console.warn('[device-id] Preferences mirror failed:', e);
  }
};

/**
 * Resolve a stable device id, preferring the OS-provided identifier on native.
 * Falls back gracefully if the Device plugin is unavailable.
 */
export const getStableDeviceId = async (): Promise<string> => {
  // 1) Native OS-backed identifier (survives reinstalls on Android via ANDROID_ID).
  if (isNativePlatform()) {
    try {
      const { Device } = await import('@capacitor/device');
      const info = await Device.getId();
      const nativeId = (info as any)?.identifier || (info as any)?.uuid;
      if (typeof nativeId === 'string' && nativeId.length > 0) {
        const stable = `native:${nativeId}`;
        // Only rewrite local mirrors if changed, to avoid pointless writes.
        try {
          if (localStorage.getItem(DEVICE_ID_KEY) !== stable) {
            writeLocal(stable);
            await mirrorToNativePrefs(stable);
          }
        } catch { /* ignore */ }
        return stable;
      }
    } catch (e) {
      console.warn('[device-id] Native Device.getId unavailable, falling back:', e);
    }
  }

  // 2) Existing localStorage id.
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
  } catch { /* ignore */ }

  // 3) Generate + persist.
  const fresh = generateId();
  writeLocal(fresh);
  await mirrorToNativePrefs(fresh);
  return fresh;
};

/**
 * Synchronous accessor kept for backwards-compat. Returns whatever is in
 * localStorage, generating a fresh UUID only if nothing is stored yet.
 * New code should prefer getStableDeviceId().
 */
export const getOrCreateDeviceId = (): string => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = generateId();
      writeLocal(id);
      void mirrorToNativePrefs(id);
    }
    return id;
  } catch {
    return generateId();
  }
};
